import {
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Play, Upload } from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type { JobCriado, Orcamento } from '../../api/tipos';
import {
  Abas,
  AreaTexto,
  Aviso,
  Botao,
  Cartao,
  cx,
  useConfirmar,
} from '../../componentes/ui';
import {
  chavesComDigitoErrado,
  estimativaDownload,
  extrairChaves,
  plural,
  reais,
} from '../../util/formato';
import {
  DuasColunas,
  Pagina,
  Pilha,
  TituloPagina,
} from '../../componentes/Pagina';
import {
  ComoCobramos,
  CustoIndefinido,
  ResumoCusto,
} from '../../componentes/ResumoCusto';
import css from './NovaBusca.module.css';

type Modo = 'colar' | 'arquivo';

const MODOS = [
  { valor: 'colar', rotulo: 'Colar chaves' },
  { valor: 'arquivo', rotulo: 'Enviar arquivo' },
] as const;

/** Mesmo teto do backend (multer): CSV texto, 200 MB cobre ~1M linhas. */
const LIMITE_ARQUIVO = 200 * 1024 * 1024;
/** Até aqui, o próprio navegador conta as chaves para mostrar o custo antes. */
const LIMITE_CONTAGEM_LOCAL = 8 * 1024 * 1024;

export function NovaBusca() {
  const navegar = useNavigate();
  const confirmar = useConfirmar();
  const [modo, setModo] = useState<Modo>('colar');
  /** Vem do <ResumoCusto>, para a confirmação usar o mesmo número exibido. */
  const [orcamento, setOrcamento] = useState<Orcamento | null>(null);
  const [texto, setTexto] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const inputArquivo = useRef<HTMLInputElement>(null);
  /** `null` = arquivo grande demais para contar no navegador. */
  const [chavesDoArquivo, setChavesDoArquivo] = useState<number | null>(null);

  // Mesma regra do servidor (44 dígitos e dígito verificador): o contador na tela precisa bater com o `total`
  // que o job vai reportar, senão a primeira impressão já é de erro.
  const chaves = useMemo(() => extrairChaves(texto), [texto]);
  const comDigitoErrado = useMemo(() => chavesComDigitoErrado(texto), [texto]);

  function usarArquivo(f: File | null) {
    setErro(null);
    setChavesDoArquivo(null);

    if (f && f.size > LIMITE_ARQUIVO) {
      setErro('Arquivo maior que 200 MB. Divida a lista em partes.');
      setArquivo(null);
      return;
    }
    setArquivo(f);

    // Só arquivos pequenos são lidos no navegador: acima disso, carregar tudo
    // em memória travaria a aba, e o ganho seria apenas antecipar um número que
    // o servidor calcula em seguida.
    if (f && f.size <= LIMITE_CONTAGEM_LOCAL) {
      void f
        .text()
        .then((t) => setChavesDoArquivo(extrairChaves(t).length))
        .catch(() => setChavesDoArquivo(null));
    }
  }

  function escolherArquivo(e: ChangeEvent<HTMLInputElement>) {
    usarArquivo(e.target.files?.[0] ?? null);
  }

  function soltarArquivo(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    setArrastando(false);
    const f = e.dataTransfer.files?.[0];
    if (f) usarArquivo(f);
  }

  async function enviar() {
    // Só confirma quando a conta é conhecida. No CSV grande o total só sai no
    // servidor, e inventar um número aqui seria pior do que não mostrar.
    if (modo === 'colar' && orcamento && chaves.length > 0) {
      const ok = await confirmar({
        titulo: `Baixar ${plural(chaves.length, 'XML', 'XMLs')} por ${reais(orcamento.totalCentavos)}?`,
        mensagem: (
          <div className={css.confirmacao}>
            <p>
              <strong>{reais(orcamento.totalCentavos)}</strong> ficam reservados
              do seu saldo enquanto a busca roda.
            </p>
            <p>
              No fim, só é cobrado o que for entregue — chave não encontrada ou
              com erro não é cobrada, e a diferença volta para o saldo.
            </p>
            <p>
              Tempo estimado: {estimativaDownload(chaves.length)}. A busca roda
              em segundo plano; pode fechar a aba.
            </p>
          </div>
        ),
        confirmar: 'Iniciar busca',
        cancelar: 'Voltar',
      });
      if (!ok) return;
    }

    setErro(null);
    setEnviando(true);
    try {
      let criado: JobCriado;
      if (modo === 'colar') {
        criado = await api<JobCriado>('/nfe/lote/chaves', {
          metodo: 'POST',
          corpo: { chaves: texto },
        });
      } else {
        if (!arquivo)
          throw new ErroApi(400, 'Escolha um arquivo .csv ou .txt.');
        const forma = new FormData();
        forma.append('arquivo', arquivo);
        criado = await api<JobCriado>('/nfe/lote', {
          metodo: 'POST',
          arquivo: forma,
        });
      }
      navegar(`/painel/downloads/${criado.jobId}`);
    } catch (err) {
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Não foi possível iniciar a busca. Tente de novo.',
      );
    } finally {
      setEnviando(false);
    }
  }

  const podeEnviar =
    !enviando && (modo === 'colar' ? chaves.length > 0 : arquivo !== null);
  /** Quantas chaves a busca terá, quando dá para saber antes de enviar. */
  const total =
    modo === 'colar' ? chaves.length : arquivo ? chavesDoArquivo : 0;

  return (
    <Pagina>
      <TituloPagina
        titulo="Nova busca"
        descricao="Traga as chaves de acesso; nós baixamos os XMLs e entregamos num ZIP."
      />

      <DuasColunas
        principal={
          <Cartao
            titulo="De onde vêm as chaves"
            rodape={
              <div className={css.rodape}>
                <span className={css.rodapeDica}>
                  A busca roda em segundo plano — pode fechar esta aba.
                </span>
                <Botao
                  icone={<Play />}
                  onClick={() => void enviar()}
                  disabled={!podeEnviar}
                  carregando={enviando}
                >
                  Iniciar busca
                </Botao>
              </div>
            }
          >
            <div className={css.corpo}>
              <div className={css.abas}>
                <Abas
                  rotulo="Origem das chaves"
                  opcoes={MODOS}
                  atual={modo}
                  aoMudar={(m) => {
                    setModo(m);
                    setErro(null);
                  }}
                />
              </div>

              {erro && <Aviso>{erro}</Aviso>}

              {modo === 'colar' ? (
                <AreaTexto
                  rotulo="Chaves de acesso"
                  rows={12}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder={
                    '35240107123456780001550010000045712345\n35240233998877660001550010000012398744'
                  }
                  acessorio={
                    <span
                      className={cx(
                        css.contador,
                        chaves.length > 0 && css.contadorAtivo,
                      )}
                    >
                      {plural(chaves.length, 'chave válida', 'chaves válidas')}
                      {comDigitoErrado > 0 &&
                        ` · ${plural(comDigitoErrado, 'ignorada', 'ignoradas')} (dígito verificador errado)`}
                    </span>
                  }
                  dica="Uma por linha, separadas por vírgula ou coladas de uma coluna de planilha — o que não for sequência de 44 dígitos é ignorado."
                />
              ) : (
                <>
                  <input
                    ref={inputArquivo}
                    type="file"
                    accept=".csv,.txt,text/csv,text/plain"
                    onChange={escolherArquivo}
                    className="sr-only"
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <div
                    className={cx(
                      css.soltar,
                      arrastando && css.arrastando,
                      arquivo && css.comArquivo,
                    )}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setArrastando(true);
                    }}
                    onDragLeave={() => setArrastando(false)}
                    onDrop={soltarArquivo}
                  >
                    <span className={css.soltarIcone} aria-hidden="true">
                      {arquivo ? <FileText /> : <Upload />}
                    </span>
                    {arquivo ? (
                      <>
                        <span className={css.soltarTitulo}>{arquivo.name}</span>
                        <span className={css.soltarDica}>
                          {(arquivo.size / 1024 / 1024)
                            .toFixed(1)
                            .replace('.', ',')}{' '}
                          MB
                          {chavesDoArquivo !== null &&
                            ` · ${plural(chavesDoArquivo, 'chave válida', 'chaves válidas')}`}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className={css.soltarTitulo}>
                          Arraste o arquivo para cá
                        </span>
                        <span className={css.soltarDica}>
                          CSV ou TXT, até 200 MB. As chaves podem estar em
                          qualquer coluna.
                        </span>
                      </>
                    )}
                    <Botao
                      aparencia="secundario"
                      pequeno
                      onClick={() => inputArquivo.current?.click()}
                    >
                      {arquivo ? 'Trocar arquivo' : 'Escolher arquivo'}
                    </Botao>
                  </div>
                  <p className={css.nota}>
                    O arquivo é lido em streaming no servidor: uma lista de um
                    milhão de chaves não precisa caber na memória.
                  </p>
                </>
              )}
            </div>
          </Cartao>
        }
        lateral={
          <Pilha>
            <Cartao titulo="Resumo da busca">
              <div className={css.resumo}>
                {modo === 'colar' ? (
                  <ResumoCusto
                    aoOrcar={setOrcamento}
                    chaves={chaves.length}
                    vazio="Nenhuma chave de 44 dígitos encontrada no texto."
                  />
                ) : !arquivo ? (
                  <p className={css.nota}>
                    Escolha um arquivo para ver o custo.
                  </p>
                ) : chavesDoArquivo === null ? (
                  <CustoIndefinido />
                ) : (
                  <ResumoCusto chaves={chavesDoArquivo} />
                )}
                {total !== null && total > 0 && (
                  <div className={css.estimativa}>
                    <span>Tempo estimado</span>
                    <strong>{estimativaDownload(total)}</strong>
                  </div>
                )}
              </div>
            </Cartao>

            <ComoCobramos contexto="Montar a busca é gratuito." />

            <Cartao titulo="O que esperar">
              <ul className={css.lista}>
                <li>Chaves repetidas são descartadas automaticamente.</li>
                <li>
                  A vazão é limitada pela fonte (~0,3 a 0,9 XML/s), então lotes
                  grandes levam horas ou dias — e continuam de onde pararam se o
                  serviço reiniciar.
                </li>
                <li>
                  Chave que não existe na fonte é marcada como{' '}
                  <strong>não encontrada</strong> e não é retentada.
                </li>
                <li>
                  Dá para baixar o ZIP parcial com a busca ainda em andamento.
                </li>
              </ul>
            </Cartao>
          </Pilha>
        }
      />
    </Pagina>
  );
}
